import { FunnelIcon } from '@phosphor-icons/react';
import { Form, Formik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, FormField, FormModal, ModalTitle, Responsive } from '@components';
import { CdnDashboardFilter, Dictionaries, FormType, InputMod, InputType, Translation, VariantType } from '@enums';
import { useAppDispatch, useTimeAndDomainFilters } from '@hooks';
import { updateFormFilter, useCdnSummary } from '@store';
import { cn, convertOptionList, getOptionListFromCatalog } from '@utils';

export const DashboardFilter = ({ currentTab, targetFrom }) => {
  const dispatch = useAppDispatch();

  const { t: tForms } = useTranslation(Translation.Configuration, { keyPrefix: 'forms.dashboard' });
  const { t: tModal } = useTranslation(Translation.Configuration, { keyPrefix: 'modals.dashboard' });

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const formType = FormType.CdnDashboard;

  const CdnConfigurationSummary = useCdnSummary();

  const { timeFilter, domainFilter } = useTimeAndDomainFilters({
    formType,
    endpoint: targetFrom,
  });

  const { options: domainOptions } = useMemo(
    () =>
      convertOptionList({
        originalOptions: CdnConfigurationSummary?.domain,
        defaultOptions: 'all',
        output: { value: 'id', label: 'domain' },
      }),
    [CdnConfigurationSummary?.domain, tForms],
  );

  const timeFilterOption = useMemo(() => getOptionListFromCatalog({ name: Dictionaries.DashboardFilter }) ?? [], []);

  const [initialValues, setInitialValues] = useState({
    [CdnDashboardFilter.Domain]: '15m',
    [CdnDashboardFilter.Time]: 'all',
  });

  useEffect(() => {
    if (timeFilter && domainFilter) {
      setInitialValues({
        [CdnDashboardFilter.Domain]: domainFilter,
        [CdnDashboardFilter.Time]: timeFilter,
      });
    }
  }, [domainFilter, timeFilter]);

  const onOptionClick = (event, setFieldValue, name) => {
    const currentOption = event.target.value;
    setFieldValue(name, currentOption);
    dispatch(
      updateFormFilter({
        formType,
        endpoint: targetFrom,
        value: [{ label: name, value: currentOption }],
      }),
    );
  };

  const handleSubmit = (values) => {
    dispatch(
      updateFormFilter({
        formType,
        endpoint: targetFrom,
        value: [
          { label: CdnDashboardFilter.Domain, value: values[CdnDashboardFilter.Domain] },
          { label: CdnDashboardFilter.Time, value: values[CdnDashboardFilter.Time] },
        ],
      }),
    );
    setIsModalOpen(false);
  };

  return (
    <Formik initialValues={initialValues} enableReinitialize onSubmit={handleSubmit}>
      {({ setFieldValue, values, resetForm }) => {
        return (
          <Form className="flex gap-3">
            <Responsive showBelow="sm">
              <Button
                className="rounded-md p-2.5 max-h-9"
                onClick={() => setIsModalOpen(true)}
                adornment={<FunnelIcon className="size-4" />}
              />
              <FormModal
                formType={formType}
                title={
                  <div className="flex items-center justify-between">
                    <ModalTitle className="font-medium">{tModal('filterBy')}</ModalTitle>
                    <Button
                      type="reset"
                      variant={VariantType.Clean}
                      className="text-slate-700 enabled:shadow-none"
                      onClick={() => resetForm()}
                    >
                      {tModal('reset')}
                    </Button>
                  </div>
                }
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                confirmButton={{
                  children: tModal('apply'),
                  type: 'submit',
                }}
              >
                <div className="flex flex-col gap-6">
                  <FormField
                    label={tForms('domain')}
                    inputType={InputType.Select}
                    inputMod={InputMod.Clean}
                    options={domainOptions}
                    name={CdnDashboardFilter.Domain}
                  />
                  <FormField
                    label={tForms('time')}
                    inputType={InputType.Custom}
                    inputMod={InputMod.Clean}
                    options={timeFilterOption}
                    fullWidth
                    name={CdnDashboardFilter.Time}
                  >
                    <div className="flex gap-2 flex-wrap">
                      {timeFilterOption.map((option, index) => {
                        const selectedValue = option.value === values[CdnDashboardFilter.Time];
                        return (
                          <Button
                            key={index}
                            variant={VariantType.Clean}
                            className={cn(
                              'px-3 py-2 border rounded-lg',
                              selectedValue
                                ? 'bg-blue-100 border-blue-500 text-blue-600'
                                : 'border-gray-300 text-gray-600',
                            )}
                            onClick={(e) => setFieldValue(CdnDashboardFilter.Time, option.value)}
                          >
                            {option.label}
                          </Button>
                        );
                      })}
                    </div>
                  </FormField>
                </div>
              </FormModal>
            </Responsive>
            <Responsive showAbove="sm">
              <FormField
                name={CdnDashboardFilter.Time}
                withoutLabel
                placeholder={tForms('selectYourTime')}
                inputType={InputType.Select}
                required
                textInputClassName="min-w-36"
                options={getOptionListFromCatalog({ name: Dictionaries.DashboardFilter })}
                onChange={(e) => onOptionClick(e, setFieldValue, CdnDashboardFilter.Time)}
              />
              <FormField
                name={CdnDashboardFilter.Domain}
                withoutLabel
                placeholder={tForms('selectYourDomain')}
                inputType={InputType.Select}
                textInputClassName="max-w-64"
                required
                options={domainOptions}
                onChange={(e) => onOptionClick(e, setFieldValue, CdnDashboardFilter.Domain)}
              />
            </Responsive>
          </Form>
        );
      }}
    </Formik>
  );
};
